import { useState, useEffect, useCallback, useMemo } from 'react';
import DateInput from './DateInput';
import { supabase } from '../lib/supabase';
import { fmt$0 } from '../utils/formatters';
import { matchTokens } from '../utils/search';
import { parseLocalDate } from '../utils/dates';
import { pdf } from '@react-pdf/renderer';
import InvoicePDF from './InvoicePDF';
import Modal from './Modal';

function levenshtein(a,b){
  const m=a.length,n=b.length;if(!m)return n;if(!n)return m;
  const d=Array.from({length:m+1},(_,i)=>i);
  for(let j=1;j<=n;j++){let prev=d[0];d[0]=j;
    for(let i=1;i<=m;i++){const tmp=d[i];d[i]=a[i-1]===b[j-1]?prev:Math.min(prev,d[i],d[i-1])+1;prev=tmp;}}
  return d[m];
}

export default function RecordSale({orgId,uid,t,si,sb,sh,sd,mn,settings,wholesaleByDate,setWholesaleByDate}){
const today=new Date().toISOString().split('T')[0];
const[customers,setCustomers]=useState([]);
const[custSearch,setCustSearch]=useState('');
const[custId,setCustId]=useState(null);
const[custName,setCustName]=useState('');
const[showCustList,setShowCustList]=useState(false);
const[dupMatches,setDupMatches]=useState(null);
const[newCustName,setNewCustName]=useState('');
const[saleDate,setSaleDate]=useState(today);
const[lines,setLines]=useState([{description:'',qty:'',unit:'each',unit_price:'',amount:0,customUnit:'',item_date:''}]);
const[tax,setTax]=useState('');
const[notes,setNotes]=useState('');
const[logToRevenue,setLogToRevenue]=useState(true);
const[editingSaleId,setEditingSaleId]=useState(null);
const[saving,setSaving]=useState(false);
const[error,setError]=useState('');
const[success,setSuccess]=useState(null);
const[dlError,setDlError]=useState('');
const[salesList,setSalesList]=useState([]);
const[salesSearch,setSalesSearch]=useState('');
const[salesFilter,setSalesFilter]=useState('all');
const[viewSale,setViewSale]=useState(null);
const[viewPdfUrl,setViewPdfUrl]=useState(null);
const[viewPdfBlob,setViewPdfBlob]=useState(null);
const[viewLoading,setViewLoading]=useState(false);
const[emailCompose,setEmailCompose]=useState(null); // {to,subject,body,invoiceNumber,blob,customerId,customerName}
const[emailSending,setEmailSending]=useState(false);
const[emailResult,setEmailResult]=useState('');
const[editCust,setEditCust]=useState(false);
const[custEdit,setCustEdit]=useState({contact_name:'',contact_email:'',contact_phone:'',billing_street:'',billing_city:'',billing_state:'',billing_zip:''});
const[custSaving,setCustSaving]=useState(false);

async function loadSales(){
  const{data}=await supabase.from('wholesale_sales').select('id,invoice_number,sale_date,total,customer_id,status,logged_to_revenue,created_at').eq('org_id',orgId).order('sale_date',{ascending:false}).order('created_at',{ascending:false});
  if(data&&customers.length>0){
    const custMap=new Map(customers.map(c=>[c.id,c.name]));
    setSalesList(data.map(s=>({...s,customer_name:custMap.get(s.customer_id)||'Unknown'})));
  }else if(data){setSalesList(data.map(s=>({...s,customer_name:''})));}
}

useEffect(()=>{
  if(!orgId)return;
  supabase.from('customers').select('id,name,contact_name,contact_email,contact_phone,billing_street,billing_city,billing_state,billing_zip').eq('org_id',orgId).eq('is_active',true).order('name').then(({data})=>{if(data)setCustomers(data);});
},[orgId]);

useEffect(()=>{if(orgId&&customers.length>0)loadSales();},[orgId,customers]);

const custNames=customers.map(c=>c.name);

function findSimilarCustomers(name){
  const nl=name.toLowerCase().trim();
  if(!nl)return[];
  const matches=[];
  for(const c of customers){
    const cl=c.name.toLowerCase();
    if(cl===nl){matches.push(c);continue;}
    if(cl.includes(nl)||nl.includes(cl)){matches.push(c);continue;}
    if(nl.length>=4&&levenshtein(nl,cl)<=2){matches.push(c);}
  }
  return matches;
}

function selectCustomer(c){
  setCustId(c.id);setCustName(c.name);setCustSearch('');setShowCustList(false);setDupMatches(null);
  setCustEdit({contact_name:c.contact_name||'',contact_email:c.contact_email||'',contact_phone:c.contact_phone||'',billing_street:c.billing_street||'',billing_city:c.billing_city||'',billing_state:c.billing_state||'',billing_zip:c.billing_zip||''});
  setEditCust(false);
}

function tryCust(name){
  const n=name.trim();if(!n)return;
  setNewCustName(n);
  const similar=findSimilarCustomers(n);
  if(similar.length>0){setDupMatches(similar);return;}
  createCustomer(n);
}

async function createCustomer(name){
  const{data,error:e}=await supabase.from('customers').insert({org_id:orgId,name}).select().single();
  if(e){setError('Failed to create customer: '+e.message);return;}
  setCustomers(p=>[...p,data]);
  selectCustomer(data);
  setDupMatches(null);setNewCustName('');
}

const filtered=custSearch.length>=1?customers.filter(c=>c.name.toLowerCase().includes(custSearch.toLowerCase())).slice(0,8):customers.slice(0,8);

function updateLine(i,field,val){
  setLines(p=>p.map((l,j)=>{
    if(j!==i)return l;
    const up={...l,[field]:val};
    const q=parseFloat(up.qty)||0;
    const p2=parseFloat(up.unit_price)||0;
    up.amount=q*p2;
    return up;
  }));
}
const UNITS=['each','dozen','case','lb','oz','gallon','quart','pint','batch','other'];
function addLine(){setLines(p=>[...p,{description:'',qty:'',unit:'each',unit_price:'',amount:0,customUnit:'',item_date:''}]);}
function removeLine(i){setLines(p=>p.length<=1?p:p.filter((_,j)=>j!==i));}

const subtotal=lines.reduce((s,l)=>s+l.amount,0);
const taxAmt=parseFloat(tax)||0;
const total=subtotal+taxAmt;

function resetForm(){
  setCustId(null);setCustName('');setCustSearch('');setSaleDate(today);
  setLines([{description:'',qty:'',unit:'each',unit_price:'',amount:0,customUnit:'',item_date:''}]);
  setTax('');setNotes('');setLogToRevenue(true);setEditingSaleId(null);setEditCust(false);
}

function buildItems(saleId){
  return lines.filter(l=>l.description||l.amount>0).map(l=>({
    org_id:orgId,sale_id:saleId,description:l.description,
    qty:parseFloat(l.qty)||0,unit:(l.unit==='other'?l.customUnit:l.unit)||null,
    unit_price:parseFloat(l.unit_price)||0,amount:l.amount,
    item_date:l.item_date||null
  }));
}

async function saveOrUpdate(targetStatus){
  if(!custId){setError('Select a customer');return;}
  if(targetStatus==='finalized'&&lines.every(l=>!l.description&&!l.amount)){setError('Add at least one line item');return;}
  setSaving(true);setError('');
  try{
    let sale;
    if(editingSaleId){
      // Update existing draft
      const{data:updated,error:e1}=await supabase.from('wholesale_sales').update({
        customer_id:custId,sale_date:saleDate,subtotal,tax:taxAmt,total,notes:notes||null,
        status:targetStatus,logged_to_revenue:targetStatus==='finalized'?logToRevenue:false
      }).eq('id',editingSaleId).select().single();
      if(e1)throw new Error(e1.message);
      sale=updated;
      // Replace line items: delete old, insert new
      await supabase.from('wholesale_sale_items').delete().eq('sale_id',editingSaleId);
    }else{
      // Insert new
      const{data:inserted,error:e1}=await supabase.from('wholesale_sales').insert({
        org_id:orgId,customer_id:custId,sale_date:saleDate,
        category:'Wholesale',subtotal,tax:taxAmt,total,notes:notes||null,
        status:targetStatus,logged_to_revenue:targetStatus==='finalized'?logToRevenue:false
      }).select().single();
      if(e1)throw new Error(e1.message);
      sale=inserted;
    }

    const items=buildItems(sale.id);
    if(items.length>0){
      const{error:e2}=await supabase.from('wholesale_sale_items').insert(items);
      if(e2){
        if(!editingSaleId)await supabase.from('wholesale_sales').delete().eq('id',sale.id);
        throw new Error('Line items failed — sale not saved. '+e2.message);
      }
    }

    if(targetStatus==='finalized'&&setWholesaleByDate&&logToRevenue){
      const dateAmts={};
      for(const li of items){const d=li.item_date||saleDate;dateAmts[d]=(dateAmts[d]||0)+li.amount;}
      setWholesaleByDate(p=>{const nv={...p};for(const[d,amt] of Object.entries(dateAmts)){nv[d]=(nv[d]||0)+amt;}return nv;});
    }

    loadSales();

    if(targetStatus==='draft'){
      setEditingSaleId(sale.id);
      setError('');
    }else{
      // Finalized — show success screen
      const{data:custRec}=await supabase.from('customers').select('id,name,contact_name,contact_email,billing_street,billing_city,billing_state,billing_zip').eq('id',custId).single();
      const savedItems=items.map(l=>({description:l.description,qty:l.qty,unit:l.unit,unitPrice:l.unit_price,amount:l.amount}));
      setSuccess({invoiceNumber:sale.invoice_number,total:sale.total,subtotal:sale.subtotal,tax:sale.tax,saleDate,notes:notes||null,customer:custRec||{name:custName},items:savedItems});
      resetForm();
    }
  }catch(e){
    setError(e.message||'Save failed');
  }finally{setSaving(false);}
}

async function loadDraftIntoForm(sale){
  setError('');
  const[{data:saleRow},{data:lineItems}]=await Promise.all([
    supabase.from('wholesale_sales').select('*').eq('id',sale.id).single(),
    supabase.from('wholesale_sale_items').select('*').eq('sale_id',sale.id).order('id'),
  ]);
  if(!saleRow)return;
  setCustId(saleRow.customer_id);setCustName(sale.customer_name||'');
  setSaleDate(saleRow.sale_date);setTax(saleRow.tax?String(saleRow.tax):'');setNotes(saleRow.notes||'');
  setLogToRevenue(saleRow.logged_to_revenue??true);setEditingSaleId(sale.id);
  if(lineItems?.length){
    setLines(lineItems.map(li=>{
      const unitVal=UNITS.includes(li.unit)?li.unit:li.unit?'other':'each';
      return{description:li.description||'',qty:li.qty?String(li.qty):'',unit:unitVal,unit_price:li.unit_price?String(li.unit_price):'',amount:Number(li.amount||0),customUnit:unitVal==='other'?li.unit:'',item_date:li.item_date||''};
    }));
  }
  // Populate custEdit for the edit panel
  const cust=customers.find(c=>c.id===saleRow.customer_id);
  if(cust){
    setCustEdit({contact_name:cust.contact_name||'',contact_email:cust.contact_email||'',contact_phone:cust.contact_phone||'',billing_street:cust.billing_street||'',billing_city:cust.billing_city||'',billing_state:cust.billing_state||'',billing_zip:cust.billing_zip||''});
  }
  // Close any open modals
  setViewSale(null);setViewPdfUrl(null);setViewPdfBlob(null);
  // Scroll to top
  window.scrollTo({top:0,behavior:'smooth'});
}

const[previewUrl,setPreviewUrl]=useState(null);
const[pdfBlob,setPdfBlob]=useState(null);

async function fetchLogoBase64(){
  const logoUrl=settings?.bizLogoUrl;
  if(!logoUrl)return'';
  try{
    const resp=await fetch(logoUrl);
    const buf=await resp.arrayBuffer();
    const u8=new Uint8Array(buf);
    let binary='';
    for(let i=0;i<u8.length;i+=8192){binary+=String.fromCharCode.apply(null,u8.subarray(i,i+8192));}
    const mime=resp.headers.get('content-type')||'image/jpeg';
    return`data:${mime};base64,${btoa(binary)}`;
  }catch(e){console.warn('Logo fetch failed:',e);return'';}
}

function buildSaleData(logoDataUri){
  return{
    biz:{name:settings?.bizName||'',address:settings?.bizAddress||'',email:settings?.bizEmail||'',logoUrl:logoDataUri},
    customer:{name:success?.customer?.name||'',contactName:success?.customer?.contact_name||'',billingStreet:success?.customer?.billing_street||'',billingCity:success?.customer?.billing_city||'',billingState:success?.customer?.billing_state||'',billingZip:success?.customer?.billing_zip||'',contactEmail:success?.customer?.contact_email||''},
    invoiceNumber:success?.invoiceNumber,saleDate:success?.saleDate,
    items:success?.items||[],subtotal:success?.subtotal,tax:success?.tax,total:success?.total,notes:success?.notes
  };
}

const fmtDate=d=>d?parseLocalDate(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'}):'—';

// Sales list filtering
const now=new Date();const thisMonthStart=`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`;
const draftCount=salesList.filter(s=>s.status==='draft').length;
const wholesaleCount=salesList.filter(s=>s.logged_to_revenue).length;
const invoiceOnlyCount=salesList.filter(s=>!s.logged_to_revenue).length;
const filteredSales=useMemo(()=>{
  let list=salesList;
  if(salesFilter==='month')list=list.filter(s=>s.sale_date>=thisMonthStart);
  else if(salesFilter==='drafts')list=list.filter(s=>s.status==='draft');
  else if(salesFilter==='wholesale')list=list.filter(s=>s.logged_to_revenue);
  else if(salesFilter==='invoiceonly')list=list.filter(s=>!s.logged_to_revenue);
  if(salesSearch)list=list.filter(s=>matchTokens(s.customer_name,salesSearch)||matchTokens(s.invoice_number||'',salesSearch));
  return list;
},[salesList,salesFilter,salesSearch,thisMonthStart]);

async function openSaleInvoice(sale){
  setViewLoading(true);setViewSale(sale);setViewPdfUrl(null);setViewPdfBlob(null);
  try{
    const[{data:saleRow},{data:lineItems},{data:custRec}]=await Promise.all([
      supabase.from('wholesale_sales').select('*').eq('id',sale.id).single(),
      supabase.from('wholesale_sale_items').select('*').eq('sale_id',sale.id).order('id'),
      supabase.from('customers').select('name,contact_name,contact_email,billing_street,billing_city,billing_state,billing_zip').eq('id',sale.customer_id).single(),
    ]);
    const logo=await fetchLogoBase64();
    const saleData={
      biz:{name:settings?.bizName||'',address:settings?.bizAddress||'',email:settings?.bizEmail||'',logoUrl:logo},
      customer:{name:custRec?.name||sale.customer_name,contactName:custRec?.contact_name||'',billingStreet:custRec?.billing_street||'',billingCity:custRec?.billing_city||'',billingState:custRec?.billing_state||'',billingZip:custRec?.billing_zip||'',contactEmail:custRec?.contact_email||''},
      invoiceNumber:saleRow?.invoice_number||sale.invoice_number,saleDate:saleRow?.sale_date||sale.sale_date,
      items:(lineItems||[]).map(l=>({description:l.description,qty:l.qty,unit:l.unit,unitPrice:l.unit_price,amount:l.amount})),
      subtotal:Number(saleRow?.subtotal||0),tax:Number(saleRow?.tax||0),total:Number(saleRow?.total||0),notes:saleRow?.notes||null
    };
    const blob=await pdf(<InvoicePDF sale={saleData}/>).toBlob();
    setViewPdfBlob(blob);setViewPdfUrl(URL.createObjectURL(blob));
  }catch(e){console.error('Failed to load invoice:',e);}
  finally{setViewLoading(false);}
}

function downloadViewPdf(){
  if(!viewPdfBlob||!viewSale)return;
  const url=URL.createObjectURL(viewPdfBlob);
  const a=document.createElement('a');a.href=url;a.download=`${viewSale.invoice_number||'invoice'}.pdf`;
  document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
}

function openEmailCompose({to,invoiceNumber,customerName,customerId,blob}){
  const bizName=settings?.bizName||'My Restaurant';
  const bizLoc=settings?.bizLoc||'';
  setEmailCompose({
    to:to||'',
    subject:`Invoice ${invoiceNumber||''} from ${bizName}`,
    body:`Hi ${customerName||''},\n\nThank you for your business. Please find invoice ${invoiceNumber||''} attached.\n\n${bizName}${bizLoc?'\n'+bizLoc:''}`,
    invoiceNumber,blob,customerId,customerName
  });
  setEmailResult('');
}

async function sendInvoiceEmail(){
  if(!emailCompose?.to){setEmailResult('Enter a recipient email');return;}
  setEmailSending(true);setEmailResult('');
  try{
    // Get blob — use provided or generate
    let blob=emailCompose.blob;
    if(!blob){setEmailResult('Invoice PDF not ready');setEmailSending(false);return;}

    // Convert blob to base64
    const buf=await blob.arrayBuffer();
    const u8=new Uint8Array(buf);
    let binary='';
    for(let i=0;i<u8.length;i+=8192){binary+=String.fromCharCode.apply(null,u8.subarray(i,i+8192));}
    const pdfBase64=btoa(binary);

    // Get session token
    const{data:{session:sess}}=await supabase.auth.getSession();
    if(!sess?.access_token)throw new Error('Not logged in');

    const res=await fetch('/api/send-invoice-email',{
      method:'POST',
      headers:{'Content-Type':'application/json','Authorization':`Bearer ${sess.access_token}`},
      body:JSON.stringify({
        org_id:orgId,to:emailCompose.to,subject:emailCompose.subject,
        body:emailCompose.body,pdfBase64,filename:`${emailCompose.invoiceNumber||'invoice'}.pdf`
      })
    });
    const data=await res.json();
    if(!res.ok)throw new Error(data.error||data.detail||'Send failed');

    // Update customer email if changed or was empty
    if(emailCompose.customerId&&emailCompose.to){
      const cust=customers.find(c=>c.id===emailCompose.customerId);
      if(!cust?.contact_email||cust.contact_email!==emailCompose.to){
        await supabase.from('customers').update({contact_email:emailCompose.to}).eq('id',emailCompose.customerId);
        setCustomers(p=>p.map(c=>c.id===emailCompose.customerId?{...c,contact_email:emailCompose.to}:c));
      }
    }

    setEmailResult(`Invoice sent to ${emailCompose.to}`);
    setTimeout(()=>{setEmailCompose(null);setEmailResult('');},2500);
  }catch(e){
    console.error('Email send failed:',e);
    setEmailResult(e.message||'Send failed');
  }finally{setEmailSending(false);}
}

async function deleteSale(sale){
  if(!window.confirm(`Delete invoice ${sale.invoice_number||''}? This can't be undone.`))return;
  // Delete line items first (safe even if CASCADE exists)
  await supabase.from('wholesale_sale_items').delete().eq('sale_id',sale.id);
  const{error:e}=await supabase.from('wholesale_sales').delete().eq('id',sale.id);
  if(e){setError('Delete failed: '+e.message);return;}
  if(setWholesaleByDate){
    setWholesaleByDate(p=>{const d=sale.sale_date;const cur=Number(p[d]||0)-Number(sale.total||0);const nv={...p};if(cur<=0)delete nv[d];else nv[d]=cur;return nv;});
  }
  loadSales();
}

// Generate preview when success is set
useEffect(()=>{
  if(!success)return;
  let cancelled=false;
  (async()=>{
    try{
      const logo=await fetchLogoBase64();
      const saleData=buildSaleData(logo);
      const blob=await pdf(<InvoicePDF sale={saleData}/>).toBlob();
      if(cancelled)return;
      setPdfBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
    }catch(e){
      console.error('PDF preview generation failed:',e);
      setDlError("Couldn't generate invoice preview");
    }
  })();
  return()=>{cancelled=true;};
},[success]);

const downloadPDF=useCallback(()=>{
  if(!pdfBlob){setDlError("PDF not ready — try again in a moment");return;}
  setDlError('');
  try{
    const url=URL.createObjectURL(pdfBlob);
    const a=document.createElement('a');a.href=url;a.download=`${success?.invoiceNumber||'invoice'}.pdf`;
    document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
  }catch(e){
    console.error('PDF download failed:',e);
    setDlError("Couldn't download invoice — try again");
  }
},[pdfBlob,success]);

if(success)return(<div>
<h1 style={{fontSize:22,fontWeight:700,margin:'0 0 20px'}}>Record Sale</h1>
<div style={{display:'flex',gap:20,alignItems:'flex-start',flexWrap:'wrap'}}>

{/* Invoice preview */}
<div style={{flex:'1 1 500px',minWidth:300}}>
{previewUrl?<iframe src={previewUrl+'#toolbar=1&navpanes=0&view=FitH'} style={{width:'100%',height:700,border:`1px solid ${t.bd}`,borderRadius:8}}/>:
<div style={{width:'100%',height:700,background:t.sf,borderRadius:8,border:`1px solid ${t.bd}`,display:'flex',alignItems:'center',justifyContent:'center',color:t.td,fontSize:13}}>{dlError||'Generating invoice preview...'}</div>}
</div>

{/* Actions sidebar */}
<div style={{flex:'0 0 280px'}}>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:20}}>
<div style={{fontSize:18,fontWeight:700,color:t.gn,marginBottom:8}}>Sale Recorded</div>
<div style={{fontSize:14,color:t.tx,marginBottom:4,fontWeight:500}}>{success.customer?.name||success.customer}</div>
<div style={{fontFamily:mn,fontSize:20,fontWeight:700,color:t.gn,marginBottom:4}}>${fmt$0(success.total)}</div>
{success.invoiceNumber&&<div style={{fontSize:12,color:t.tm,marginBottom:16}}>Invoice #{success.invoiceNumber}</div>}
<div style={{display:'flex',flexDirection:'column',gap:8}}>
<button onClick={downloadPDF} disabled={!pdfBlob} style={{...sb,background:pdfBlob?'#8B1A1A':'#888',color:'#fff',padding:'10px 20px',fontSize:13,opacity:pdfBlob?1:0.6}}>Download Invoice</button>
<button onClick={()=>openEmailCompose({to:success.customer?.contact_email||'',invoiceNumber:success.invoiceNumber,customerName:success.customer?.name||'',customerId:success.customer?.id||null,blob:pdfBlob})} disabled={!pdfBlob} style={{...sb,background:pdfBlob?t.bl:'#888',color:'#fff',padding:'10px 20px',fontSize:13,opacity:pdfBlob?1:0.6}}>Email to Customer</button>
<button onClick={()=>{setSuccess(null);setPreviewUrl(null);setPdfBlob(null);setDlError('');}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:'10px 20px',fontSize:13}}>Done</button>
</div>
{dlError&&<div style={{fontSize:11,color:t.ac,marginTop:10}}>{dlError}</div>}
</div>
</div>
</div>
</div>);

return(<div>
<h1 style={{fontSize:22,fontWeight:700,margin:'0 0 20px'}}>Record Sale</h1>

<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:18,marginBottom:16,overflow:'hidden'}}>

{/* Log to revenue segmented control — top right */}
<div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4,marginBottom:10}}>
<div style={{display:'flex',borderRadius:8,overflow:'hidden',border:`1px solid ${t.bd}`}}>
<button onClick={()=>setLogToRevenue(true)} style={{padding:'5px 12px',fontSize:10,fontWeight:500,border:'none',cursor:'pointer',background:logToRevenue?t.gn:t.sf,color:logToRevenue?'#fff':t.tm,fontFamily:"'DM Sans',sans-serif",transition:'background 0.15s'}}>Log to Wholesale</button>
<button onClick={()=>setLogToRevenue(false)} style={{padding:'5px 12px',fontSize:10,fontWeight:500,border:'none',borderLeft:`1px solid ${t.bd}`,cursor:'pointer',background:!logToRevenue?t.ac:t.sf,color:!logToRevenue?'#fff':t.tm,fontFamily:"'DM Sans',sans-serif",transition:'background 0.15s'}}>Invoice Only</button>
</div>
<div style={{fontSize:8,color:t.td}}>{logToRevenue?'Sale counts in wholesale revenue reports':'Already paid at the register — just need the invoice'}</div>
</div>

{/* Customer picker */}
<div style={{marginBottom:14}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Customer</label>
{custId?<div style={{marginTop:4}}>
<div style={{display:'flex',alignItems:'center',gap:8}}>
<span style={{fontSize:14,fontWeight:600}}>{custName}</span>
{custEdit.contact_name&&<span style={{fontSize:10,color:t.td}}>{custEdit.contact_name}</span>}
<button onClick={()=>setEditCust(p=>!p)} style={{background:'none',border:'none',color:t.bl,cursor:'pointer',fontSize:10,fontFamily:"'DM Sans',sans-serif"}}>{editCust?'Close':'Edit details'}</button>
<button onClick={()=>{setCustId(null);setCustName('');setCustSearch('');setEditCust(false);}} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10}}>✕ change</button>
</div>
{editCust&&<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:8,maxWidth:500}}>
<div><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>Contact Name</label><input value={custEdit.contact_name} onChange={e=>setCustEdit(p=>({...p,contact_name:e.target.value}))} placeholder="Jane Smith" style={{...si,width:'100%'}}/></div>
<div><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>Email</label><input value={custEdit.contact_email} onChange={e=>setCustEdit(p=>({...p,contact_email:e.target.value}))} placeholder="jane@example.com" style={{...si,width:'100%'}}/></div>
<div><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>Phone</label><input value={custEdit.contact_phone} onChange={e=>setCustEdit(p=>({...p,contact_phone:e.target.value}))} placeholder="(207) 555-1234" style={{...si,width:'100%'}}/></div>
<div style={{gridColumn:'1/-1'}}><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>Street</label><input value={custEdit.billing_street} onChange={e=>setCustEdit(p=>({...p,billing_street:e.target.value}))} placeholder="123 Main St" style={{...si,width:'100%'}}/></div>
<div><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>City</label><input value={custEdit.billing_city} onChange={e=>setCustEdit(p=>({...p,billing_city:e.target.value}))} placeholder="Bar Harbor" style={{...si,width:'100%'}}/></div>
<div style={{display:'flex',gap:6}}>
<div style={{flex:1}}><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>State</label><input value={custEdit.billing_state} onChange={e=>setCustEdit(p=>({...p,billing_state:e.target.value}))} placeholder="ME" style={{...si,width:'100%'}}/></div>
<div style={{flex:1}}><label style={{fontSize:8,color:t.td,textTransform:'uppercase'}}>Zip</label><input value={custEdit.billing_zip} onChange={e=>setCustEdit(p=>({...p,billing_zip:e.target.value}))} placeholder="04609" style={{...si,width:'100%'}}/></div>
</div>
<div style={{gridColumn:'1/-1',display:'flex',gap:6}}>
<button disabled={custSaving} onClick={async()=>{setCustSaving(true);const{contact_name,contact_email,contact_phone,billing_street,billing_city,billing_state,billing_zip}=custEdit;const{error:e}=await supabase.from('customers').update({contact_name,contact_email,contact_phone,billing_street,billing_city,billing_state,billing_zip}).eq('id',custId);setCustSaving(false);if(e){setError('Save failed: '+e.message);return;}setCustomers(p=>p.map(c=>c.id===custId?{...c,...custEdit}:c));setEditCust(false);}} style={{...sb,background:t.gn,fontSize:10,padding:'4px 12px',opacity:custSaving?0.6:1}}>{custSaving?'Saving...':'Save'}</button>
<button onClick={()=>setEditCust(false)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10,padding:'4px 12px'}}>Cancel</button>
</div>
</div>}
</div>:
<div style={{position:'relative'}}>
<input value={custSearch} onChange={e=>{setCustSearch(e.target.value);setShowCustList(true);}} onFocus={()=>setShowCustList(true)} placeholder="Search or add customer..." style={{...si,width:'100%',maxWidth:400}}/>
{showCustList&&<div style={{position:'absolute',top:'100%',left:0,right:0,maxWidth:400,background:t.cd,border:`1px solid ${t.bd}`,borderRadius:8,maxHeight:240,overflow:'auto',zIndex:10,boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
{filtered.map(c=><div key={c.id} onClick={()=>selectCustomer(c)} style={{padding:'8px 12px',cursor:'pointer',borderBottom:`1px solid ${t.bd}`,fontSize:12}} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
{c.name}{c.contact_name&&<span style={{fontSize:10,color:t.td,marginLeft:8}}>{c.contact_name}</span>}
</div>)}
{custSearch.trim()&&!customers.some(c=>c.name.toLowerCase()===custSearch.trim().toLowerCase())&&<div onClick={()=>tryCust(custSearch)} style={{padding:'8px 12px',cursor:'pointer',fontSize:12,color:t.bl,fontWeight:500}}>+ Add "{custSearch.trim()}" as new customer</div>}
{filtered.length===0&&!custSearch.trim()&&<div style={{padding:'8px 12px',fontSize:11,color:t.td}}>No customers yet</div>}
</div>}
</div>}
</div>

{/* "Did you mean?" modal */}
{dupMatches&&<div style={{background:'rgba(212,168,36,0.06)',border:`1px solid ${t.yl}`,borderRadius:8,padding:16,marginBottom:14}}>
<div style={{fontSize:14,fontWeight:700,marginBottom:4}}>Did you mean one of these?</div>
<div style={{fontSize:11,color:t.td,marginBottom:10}}>"{newCustName}" looks similar to existing customers:</div>
{dupMatches.map(c=><div key={c.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 10px',borderBottom:`1px solid ${t.bd}`,borderRadius:4}}>
<span style={{fontSize:13,fontWeight:500}}>{c.name}</span>
<button onClick={()=>selectCustomer(c)} style={{...sb,background:t.bl,fontSize:10,padding:'4px 10px'}}>Use this one</button>
</div>)}
<div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
<button onClick={()=>setDupMatches(null)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:11}}>Cancel</button>
<button onClick={()=>createCustomer(newCustName)} style={{...sb,background:t.gn,fontSize:11}}>Create "{newCustName}" anyway</button>
</div>
</div>}

{/* Sale date */}
<div style={{marginBottom:14}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Sale Date</label>
<DateInput value={saleDate} onChange={e=>setSaleDate(e.target.value)} style={{...si,width:180,display:'block',marginTop:4}}/>
</div>

{/* Line items */}
<div style={{marginBottom:14}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1,marginBottom:6,display:'block'}}>Line Items</label>
<div style={{overflowX:'auto'}}>
<table style={{width:'100%',minWidth:620,borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>
{['Date','Description','Qty','Unit','Unit Price','Amount',''].map(h=><th key={h} style={{...sh,fontSize:9}}>{h}</th>)}
</tr></thead>
<tbody>{lines.map((l,i)=>{const unitLabel=l.unit==='other'?(l.customUnit||'unit'):l.unit;const vt={verticalAlign:'top'};return<tr key={i} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{...sd,...vt,width:90}}><DateInput value={l.item_date||''} onChange={e=>updateLine(i,'item_date',e.target.value)} style={{...si,width:'100%',fontSize:9}} title="Optional: date for this line item"/></td>
<td style={{...sd,...vt}}><input value={l.description} onChange={e=>updateLine(i,'description',e.target.value)} placeholder="Item description" style={{...si,width:'100%'}}/></td>
<td style={{...sd,...vt,width:60}}><input value={l.qty} onChange={e=>updateLine(i,'qty',e.target.value)} placeholder="0" style={{...si,width:'100%',fontFamily:mn,textAlign:'right'}}/></td>
<td style={{...sd,...vt,width:l.unit==='other'?120:80}}><select value={l.unit} onChange={e=>updateLine(i,'unit',e.target.value)} style={{...si,width:l.unit==='other'?55:'100%'}}>{UNITS.map(u=><option key={u} value={u}>{u}</option>)}</select>{l.unit==='other'&&<input value={l.customUnit||''} onChange={e=>updateLine(i,'customUnit',e.target.value)} placeholder="unit" style={{...si,width:55,marginLeft:4,fontSize:10}}/>}</td>
<td style={{...sd,...vt,width:95}}><div><input value={l.unit_price} onChange={e=>updateLine(i,'unit_price',e.target.value)} placeholder="0.00" style={{...si,width:'100%',fontFamily:mn,textAlign:'right'}}/><div style={{fontSize:8,color:t.td,textAlign:'right',marginTop:1}}>per {unitLabel}</div></div></td>
<td style={{...sd,...vt,width:80,fontFamily:mn,textAlign:'right',fontSize:12}}>${fmt$0(l.amount)}</td>
<td style={{...sd,...vt,width:28}}>{lines.length>1&&<button onClick={()=>removeLine(i)} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:12}}>✕</button>}</td>
</tr>;})}</tbody>
</table>
</div>
<button onClick={addLine} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10,marginTop:8}}>+ Add Line</button>
</div>

{/* Totals */}
<div style={{display:'flex',justifyContent:'flex-end',gap:24,marginBottom:14}}>
<div>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Subtotal</div>
<div style={{fontFamily:mn,fontSize:16,fontWeight:600,textAlign:'right'}}>${fmt$0(subtotal)}</div>
</div>
<div>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Tax</div>
<input value={tax} onChange={e=>setTax(e.target.value)} placeholder="0.00" style={{...si,width:80,fontFamily:mn,textAlign:'right'}}/>
</div>
<div>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Total</div>
<div style={{fontFamily:mn,fontSize:18,fontWeight:700,color:t.gn,textAlign:'right'}}>${fmt$0(total)}</div>
</div>
</div>

{/* Notes */}
<div style={{marginBottom:14}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Notes (optional)</label>
<input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. delivery instructions, PO reference" style={{...si,width:'100%',marginTop:4}}/>
</div>

{/* Save / Draft / Finalize */}
{editingSaleId&&<div style={{fontSize:11,color:t.yl,marginBottom:8}}>Editing draft{editingSaleId?' — changes update the existing invoice':''}</div>}
<div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
{error&&<div style={{fontSize:11,color:t.ac,alignSelf:'center',marginRight:'auto'}}>{error}</div>}
{editingSaleId&&<button onClick={resetForm} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,padding:'10px 16px',fontSize:12}}>Cancel</button>}
<button onClick={()=>saveOrUpdate('draft')} disabled={saving} style={{...sb,background:saving?'#888':t.sf,color:t.yl,border:`1px solid ${t.yl}`,padding:'10px 16px',opacity:saving?0.6:1,fontSize:12}}>{saving?'Saving...':editingSaleId?'Save Draft':'Save as Draft'}</button>
<button onClick={()=>saveOrUpdate('finalized')} disabled={saving} style={{...sb,background:saving?'#888':t.gn,color:'#fff',padding:'10px 24px',opacity:saving?0.6:1}}>{saving?'Saving...':(editingSaleId?'Finalize':'Save Sale')}</button>
</div>
</div>

{/* Sales history */}
{salesList.length>0&&<div style={{marginTop:24}}>
<div style={{fontSize:14,fontWeight:600,marginBottom:10}}>Sales History</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:14}}>

{/* Search + filter chips */}
<div style={{display:'flex',gap:8,marginBottom:10,alignItems:'center',flexWrap:'wrap'}}>
<div style={{display:'flex',gap:6}}>
{[['all','All ('+salesList.length+')'],['month','This Month ('+salesList.filter(s=>s.sale_date>=thisMonthStart).length+')'],...(draftCount>0?[['drafts','Drafts ('+draftCount+')']]:[]),['wholesale','Wholesale ('+wholesaleCount+')'],['invoiceonly','Invoice Only ('+invoiceOnlyCount+')']].map(([k,l])=>{const ac=salesFilter===k;const clr=k==='drafts'?t.yl:k==='wholesale'?t.gn:k==='invoiceonly'?t.ac:t.bl;return<button key={k} onClick={()=>setSalesFilter(k)} style={{padding:'4px 10px',borderRadius:12,fontSize:10,cursor:'pointer',border:`1px solid ${ac?clr:t.bd}`,background:ac?clr:t.sf,color:ac?'#fff':t.tm,fontFamily:"'DM Sans',sans-serif"}}>{l}</button>;})}
</div>
<div style={{flex:1,maxWidth:280,position:'relative'}}>
<input value={salesSearch} onChange={e=>setSalesSearch(e.target.value)} placeholder="Search by customer or invoice #..." style={{...si,width:'100%',paddingRight:28,boxSizing:'border-box',fontSize:11}}/>
{salesSearch&&<button onClick={()=>setSalesSearch('')} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:t.tm,fontSize:14,cursor:'pointer',padding:'2px 4px'}}>✕</button>}
</div>
<div style={{marginLeft:'auto',fontSize:11,color:t.tm}}>{filteredSales.length} sale{filteredSales.length!==1?'s':''}</div>
</div>

{/* Table */}
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>
{['Invoice #','Customer','Date','Total',''].map(h=><th key={h} style={sh}>{h}</th>)}
</tr></thead>
<tbody>{filteredSales.map(s=><tr key={s.id} style={{borderBottom:`1px solid ${t.bd}`,cursor:'pointer'}} onClick={()=>s.status==='draft'?loadDraftIntoForm(s):openSaleInvoice(s)} onMouseOver={e=>e.currentTarget.style.background=t.sfh||t.sf} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<td style={{...sd,fontSize:12,fontWeight:500,color:t.bl}}>{s.invoice_number||'—'}{s.status==='draft'&&<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(212,168,36,0.15)',color:t.yl,marginLeft:6,fontWeight:600}}>DRAFT</span>}{s.logged_to_revenue?<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(34,197,94,0.1)',color:t.gn,marginLeft:6,fontWeight:600}}>Wholesale</span>:<span style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'rgba(200,55,45,0.08)',color:t.ac,marginLeft:6,fontWeight:600}}>Invoice Only</span>}</td>
<td style={{...sd,fontSize:12}}>{s.customer_name}</td>
<td style={{...sd,fontSize:11,color:t.tm}}>{fmtDate(s.sale_date)}</td>
<td style={{...sd,fontFamily:mn,fontSize:12,fontWeight:600,color:t.gn}}>${fmt$0(s.total)}</td>
<td style={{...sd,width:30}} onClick={e=>e.stopPropagation()}><button onClick={()=>deleteSale(s)} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:12}} title="Delete sale">✕</button></td>
</tr>)}</tbody>
</table>
{filteredSales.length===0&&<div style={{textAlign:'center',padding:20,color:t.td,fontSize:11}}>No matching sales</div>}
</div>
</div>}

{/* View invoice modal */}
{viewSale&&<Modal open={true} onClose={()=>{setViewSale(null);setViewPdfUrl(null);setViewPdfBlob(null);}} width={800} t={t}>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
<div>
<div style={{fontSize:16,fontWeight:700}}>Invoice {viewSale.invoice_number||''}</div>
<div style={{fontSize:11,color:t.tm}}>{viewSale.customer_name} — {fmtDate(viewSale.sale_date)}</div>
</div>
<div style={{display:'flex',gap:8}}>
<button onClick={downloadViewPdf} disabled={!viewPdfBlob} style={{...sb,background:viewPdfBlob?'#8B1A1A':'#888',color:'#fff',fontSize:11,padding:'6px 14px',opacity:viewPdfBlob?1:0.6}}>Download</button>
<button onClick={()=>{const cust=customers.find(c=>c.id===viewSale.customer_id);openEmailCompose({to:cust?.contact_email||'',invoiceNumber:viewSale.invoice_number,customerName:viewSale.customer_name,customerId:viewSale.customer_id,blob:viewPdfBlob});}} disabled={!viewPdfBlob} style={{...sb,background:viewPdfBlob?t.bl:'#888',color:'#fff',fontSize:11,padding:'6px 14px',opacity:viewPdfBlob?1:0.6}}>Email</button>
</div>
</div>
{viewLoading?<div style={{textAlign:'center',padding:40,color:t.td}}>Loading invoice...</div>:
viewPdfUrl?<iframe src={viewPdfUrl+'#toolbar=0&navpanes=0&view=FitH'} style={{width:'100%',height:600,border:`1px solid ${t.bd}`,borderRadius:6}}/>:
<div style={{textAlign:'center',padding:40,color:t.td}}>Failed to load invoice</div>}
</Modal>}

{/* Email compose panel */}
{emailCompose&&<Modal open={true} onClose={()=>{setEmailCompose(null);setEmailResult('');}} width={480} t={t}>
<div style={{fontSize:16,fontWeight:700,marginBottom:12}}>Email Invoice {emailCompose.invoiceNumber||''}</div>
<div style={{marginBottom:10}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>To</label>
<input value={emailCompose.to} onChange={e=>setEmailCompose(p=>({...p,to:e.target.value}))} placeholder="customer@example.com" style={{...si,width:'100%',marginTop:2}}/>
</div>
<div style={{marginBottom:10}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Subject</label>
<input value={emailCompose.subject} onChange={e=>setEmailCompose(p=>({...p,subject:e.target.value}))} style={{...si,width:'100%',marginTop:2}}/>
</div>
<div style={{marginBottom:14}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',letterSpacing:1}}>Message</label>
<textarea value={emailCompose.body} onChange={e=>setEmailCompose(p=>({...p,body:e.target.value}))} rows={6} style={{...si,width:'100%',marginTop:2,resize:'vertical',fontFamily:"'DM Sans',sans-serif"}}/>
</div>
<div style={{fontSize:10,color:t.td,marginBottom:12}}>Invoice PDF will be attached automatically.</div>
<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
{emailResult&&<div style={{fontSize:11,color:emailResult.startsWith('Invoice sent')?t.gn:t.ac,alignSelf:'center',marginRight:'auto'}}>{emailResult}</div>}
<button onClick={()=>{setEmailCompose(null);setEmailResult('');}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:12}}>Cancel</button>
<button onClick={sendInvoiceEmail} disabled={emailSending} style={{...sb,background:emailSending?'#888':t.gn,color:'#fff',fontSize:12,padding:'8px 20px',opacity:emailSending?0.6:1}}>{emailSending?'Sending...':'Send'}</button>
</div>
</Modal>}
</div>);
}
